import type { ReactElement } from 'react';

import type { Cohort, Course } from '@/features/masters/api';

export interface MemberFormValues {
  member_id: string;
  /** 登録と同時に招待メールを送る宛先（新規登録のときだけ使う / D-122） */
  invite_email: string;
  name: string;
  name_kana: string;
  company: string;
  position: string;
  specialty: string;
  cohort_id: string;
  course_id: string;
  bio: string;
  /** 先方のマイページ様式に合わせた公開プロフィール項目（D-148） */
  company_url: string;
  subordinate_count: string;
  years_of_service: string;
  hobbies: string;
  career_history: string;
  participation_purpose: string;
  strengths: string;
  weaknesses: string;
  goals: string;
  phone: string;
  contact_email: string;
  tags: string;
}

export const EMPTY_MEMBER_FORM: MemberFormValues = {
  member_id: '',
  invite_email: '',
  name: '',
  name_kana: '',
  company: '',
  position: '',
  specialty: '',
  cohort_id: '',
  course_id: '',
  bio: '',
  company_url: '',
  subordinate_count: '',
  years_of_service: '',
  hobbies: '',
  career_history: '',
  participation_purpose: '',
  strengths: '',
  weaknesses: '',
  goals: '',
  phone: '',
  contact_email: '',
  tags: '',
};

/** 会員の作成・編集で共通する入力欄。M3 共通部品ではないが両画面で使い回す。 */
export function MemberFormFields({
  values,
  onChange,
  cohorts,
  courses,
  memberIdEditable,
}: {
  values: MemberFormValues;
  onChange: (values: MemberFormValues) => void;
  cohorts: Cohort[];
  courses: Course[];
  memberIdEditable: boolean;
}): ReactElement {
  function set<K extends keyof MemberFormValues>(key: K, value: MemberFormValues[K]): void {
    onChange({ ...values, [key]: value });
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <p className="text-wptext-muted sm:col-span-2">
        <span className="text-wpnotice-error">*</span> が付いている項目は必ず入力してください。
        {memberIdEditable
          ? ' 分かっている項目はここで入れておけます。空のままにした項目は、招待を受けたご本人が入力します。'
          : ' それ以外は後からでも登録できます。'}
      </p>
      <Field
        label="会員No."
        hint={
          memberIdEditable
            ? '登録するとシステムが自動で採番します（例: M-0001）。あとから変更はできません。'
            : '登録時にシステムが採番した番号です。変更はできません。'
        }
      >
        <input
          value={memberIdEditable ? '登録時に自動で採番されます' : values.member_id}
          disabled
          className="w-full rounded-sm border border-wpborder px-3 py-2 disabled:bg-wpcanvas"
        />
      </Field>
      <Field label="氏名" required>
        <input
          value={values.name}
          onChange={(e) => set('name', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      {memberIdEditable && (
        <Field
          label="メールアドレス（招待を送る先）"
          required
          hint="登録すると、このアドレスに招待メールが届きます。受け取った本人がパスワードを決め、プロフィールの続きを入力します。"
          span2
        >
          <input
            type="email"
            value={values.invite_email}
            onChange={(e) => set('invite_email', e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          />
        </Field>
      )}
      <Field label="ふりがな">
        <input
          value={values.name_kana}
          onChange={(e) => set('name_kana', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="期" required hint="受講生は必ず1つの期に所属します。">
        <select
          required
          value={values.cohort_id}
          onChange={(e) => set('cohort_id', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        >
          <option value="">選択してください</option>
          {cohorts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {!c.is_active ? '（無効）' : ''}
            </option>
          ))}
        </select>
      </Field>
      <Field label="コース" required hint="受講生は必ず1つのコースに所属します。">
        <select
          required
          value={values.course_id}
          onChange={(e) => set('course_id', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        >
          <option value="">選択してください</option>
          {courses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {!c.is_active ? '（無効）' : ''}
            </option>
          ))}
        </select>
      </Field>
      <Field label="企業名">
        <input
          value={values.company}
          onChange={(e) => set('company', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="役職">
        <input
          value={values.position}
          onChange={(e) => set('position', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="管掌領域" hint="例: 事業戦略・IT／新規事業・製造。順位一覧や受講生同士のプロフィールに表示されます。">
        <input
          value={values.specialty}
          onChange={(e) => set('specialty', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="企業HP">
        <input
          type="url"
          value={values.company_url}
          onChange={(e) => set('company_url', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="部下人数">
        <input
          type="number"
          min={0}
          value={values.subordinate_count}
          onChange={(e) => set('subordinate_count', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="勤続年数">
        <input
          type="number"
          min={0}
          value={values.years_of_service}
          onChange={(e) => set('years_of_service', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="電話番号">
        <input
          type="tel"
          value={values.phone}
          onChange={(e) => set('phone', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="連絡先メール" hint="ログインに使うメールアドレスとは別に登録できます。">
        <input
          type="email"
          value={values.contact_email}
          onChange={(e) => set('contact_email', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field
        label="タグ"
        span2
        hint="対象者の絞り込みに使う目印です。複数付けるときは読点ではなくカンマで区切ります（例: 製造業,第3期,関西）。"
      >
        <input
          value={values.tags}
          onChange={(e) => set('tags', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="趣味・特技・最近のマイブーム" span2>
        <textarea
          rows={3}
          value={values.hobbies}
          onChange={(e) => set('hobbies', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="経歴" span2>
        <textarea
          rows={3}
          value={values.career_history}
          onChange={(e) => set('career_history', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="CXO Growth参加目的" span2>
        <textarea
          rows={3}
          value={values.participation_purpose}
          onChange={(e) => set('participation_purpose', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="強み" span2>
        <textarea
          rows={3}
          value={values.strengths}
          onChange={(e) => set('strengths', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="弱み" span2>
        <textarea
          rows={3}
          value={values.weaknesses}
          onChange={(e) => set('weaknesses', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="目標" span2>
        <textarea
          rows={3}
          value={values.goals}
          onChange={(e) => set('goals', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
      <Field label="自己紹介" span2>
        <textarea
          rows={4}
          value={values.bio}
          onChange={(e) => set('bio', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
    </div>
  );
}

function Field({
  label,
  required,
  span2,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  span2?: boolean;
  /** 何を入れる欄なのかが自明でないときの補足（D-083） */
  hint?: string;
  children: ReactElement;
}): ReactElement {
  return (
    <label className={`block ${span2 ? 'sm:col-span-2' : ''}`}>
      <span className="mb-1.5 block text-wptext-muted">
        {label}
        {required && <span className="text-wpnotice-error"> *</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-wptext-muted">{hint}</span>}
    </label>
  );
}
